import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getProfitByDate, upsertProfitEntry } from "@/lib/api";
import { formatINR, todayISO } from "@/lib/format";
import { computeProfit, PROFIT_FIELDS, FORMULA_LINES } from "@/lib/profitCalc";
import { useBranch } from "@/context/BranchContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calculator, Save, AlertTriangle, ArrowRight } from "lucide-react";
import { MoneyField, Row } from "./ProfitUI";

const SHOP_FIELDS = [
  ["earning_for_shop", "Earning for Shop", "Shop's earnings for the day"],
  ["price_to_shop", "Price to Shop", "Amount billed to the shop"],
];
const DC_FIELDS = [
  ["dc_collected", "DC Collected", "Delivery charge collected from customers"],
  ["dc_from_shop", "DC from Shop", "Delivery charge received from the shop"],
  ["dc_payable", "DC Payable", "Delivery charge payable"],
];

const emptyForm = () => ({ ...Object.fromEntries(PROFIT_FIELDS.map((k) => [k, ""])), notes: "" });

function FieldGroup({ title, fields, form, setField }) {
  return (
    <div className="space-y-3">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {fields.map(([k, label, hint]) => (
          <MoneyField key={k} label={label} hint={hint} value={form[k]} onChange={(v) => setField(k, v)} testId={`profit-${k.replaceAll("_", "-")}-input`} />
        ))}
      </div>
    </div>
  );
}

function LiveSummary({ form, preview }) {
  const n = (k) => parseFloat(form[k]) || 0;
  return (
    <section className="bg-white rounded-xl border border-zinc-200 p-6 space-y-4" data-testid="profit-live-summary">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-medium">Live Calculation</h2>
        <Calculator className="h-4 w-4 text-emerald-700" />
      </div>
      <div className="space-y-2 text-sm tabular">
        <Row label="Earning for Shop" value={n("earning_for_shop")} />
        <Row label="Price to Shop" value={n("price_to_shop")} sign="−" />
        <Row label="Total Commission" value={preview.total_commission} strong tone={preview.total_commission < 0 ? "danger" : "positive"} testId="profit-live-total-commission" />
        <Row label="Ciboee Commission (5%)" value={preview.ciboee_commission} sign="−" tone="warning" testId="profit-live-ciboee" />
        <div className="flex items-center gap-2 pt-1"><ArrowRight className="h-3.5 w-3.5 text-emerald-600" /><div className="flex-1 h-px bg-zinc-100" /></div>
        <Row label="Total DC Collected" value={preview.total_dc_collected} testId="profit-live-total-dc" />
        <Row label="DC Payable" value={n("dc_payable")} sign="−" />
        <Row label="DC Loss" value={preview.dc_loss} sign="−" tone="warning" testId="profit-live-dc-loss" />
        <Row label="Minimum Guarantee" value={preview.min_guarantee} sign="−" tone="warning" testId="profit-live-mg" />
        <Row label="Other Expense" value={n("other_expense")} sign="−" />
        <div className="pt-3 border-t border-zinc-200 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Final Profit</div>
          <div className={`font-display text-2xl font-semibold tabular ${preview.final_profit >= 0 ? "text-emerald-700" : "text-rose-600"}`} data-testid="profit-live-profit">
            {formatINR(preview.final_profit)}
          </div>
        </div>
        {preview.final_profit < 0 && (
          <p className="text-[11px] text-rose-600 inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Negative profit for this day.</p>
        )}
      </div>
      <div className="pt-3 border-t border-zinc-200 text-[11px] text-zinc-500 leading-relaxed space-y-0.5">
        {FORMULA_LINES.map((l) => <div key={l}>{l}</div>)}
      </div>
    </section>
  );
}

export function DailyProfitTab() {
  const { branchId, activeBranch } = useBranch();
  const [date, setDate] = useState(todayISO());
  const [pe, setPe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!branchId) return;
    getProfitByDate(date).then((data) => {
      setPe(data);
      setForm({ ...Object.fromEntries(PROFIT_FIELDS.map((k) => [k, data[k] || ""])), notes: data.notes || "" });
    });
  }, [date, branchId]);

  const preview = useMemo(() => computeProfit(form), [form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { entry_date: date, notes: form.notes || "" };
      PROFIT_FIELDS.forEach((k) => (payload[k] = parseFloat(form[k]) || 0));
      setPe(await upsertProfitEntry(payload));
      toast.success(`Saved for ${activeBranch?.name || "branch"} · ${date}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <section className="xl:col-span-2 bg-white rounded-xl border border-zinc-200 p-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-lg font-medium">Daily figures · {activeBranch?.name || "…"}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">One entry per branch per day — saving again updates it. Enter the minimum guarantee paid manually.</p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="profit-date-input" />
          </div>
        </div>
        {pe?.id && <div className="text-[11px] text-emerald-700" data-testid="profit-existing-badge">Existing entry loaded — editing will update it.</div>}
        <FieldGroup title="Shop" fields={SHOP_FIELDS} form={form} setField={setField} />
        <FieldGroup title="Delivery Charges (DC)" fields={DC_FIELDS} form={form} setField={setField} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MoneyField label="Minimum Guarantee" hint="Total MG paid to delivery boys" value={form.min_guarantee} onChange={(v) => setField("min_guarantee", v)} testId="profit-min-guarantee-input" />
          <MoneyField label="Other Expense" hint="Fuel, packing, etc." value={form.other_expense} onChange={(v) => setField("other_expense", v)} testId="profit-other-expense-input" />
          <div className="space-y-1.5 md:col-span-3">
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="profit-notes-input" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || !branchId} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="profit-save-btn">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </section>
      <LiveSummary form={form} preview={preview} />
    </div>
  );
}
